import { useEffect, useState } from 'react';
import { Bot, RefreshCw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface SystemPromptPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string | null;
  workspaceId: string | null;
  templateId?: string | null;
  topic?: string | null;
  getToken: () => Promise<string | null>;
}

export function SystemPromptPanel({
  open,
  onOpenChange,
  sessionId,
  workspaceId,
  templateId,
  topic,
  getToken,
}: SystemPromptPanelProps) {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPrompt = async () => {
    if (!sessionId || !workspaceId) {
      setPrompt(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params = new URLSearchParams({
        sessionId,
        workspaceId,
      });
      if (templateId) params.set('templateId', templateId);
      if (topic) params.set('topic', topic);

      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(
        `/api/python/interview/realtime/preview-prompt?${params.toString()}`,
        { headers },
      );

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }

      const data = (await res.json()) as { prompt: string };
      setPrompt(data.prompt);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Fetch whenever the panel opens or the session context changes
  useEffect(() => {
    if (open) void fetchPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId, workspaceId, templateId, topic]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-[520px] max-w-full flex-col gap-0 p-0 sm:max-w-[520px]"
      >
        <SheetHeader className="flex flex-row items-center gap-2 border-b border-border px-4 py-3 pr-12">
          <Bot className="h-4 w-4 shrink-0 text-primary" />
          <SheetTitle className="text-sm font-semibold">System-Prompt (Realtime)</SheetTitle>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-7 w-7 shrink-0"
            onClick={() => void fetchPrompt()}
            disabled={loading}
            title="Aktualisieren"
            aria-label="System-Prompt aktualisieren"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
          <p className="text-xs text-muted-foreground">
            Dieser Prompt wird bei jeder Realtime-Sitzung an Marvin gesendet. Er enthält
            Ontologie-Konzepte, Wissensgraph-Kontext und sprachspezifische Regeln.
          </p>

          {loading && (
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          {!loading && !error && prompt !== null && (
            <textarea
              readOnly
              value={prompt}
              className="flex-1 resize-none rounded-md border border-border bg-muted/50 p-3 font-mono text-[11px] leading-relaxed text-foreground outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          )}

          {!loading && !error && prompt === null && (
            <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
              Kein aktives Interview ausgewählt.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
